/**
 * GA4 Analytics Configuration for Merchants
 * Allows merchants to configure Google Analytics 4 tracking for their storefront
 * This is a PRO feature - requires analytics.ga4 feature access
 *
 * Uses toggle-based feature gating: toggle is always visible, but enabling
 * checks tier and shows pricing modal if user doesn't have access.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { navigateToUpgrade } from '../subscription/upgradeNav';
import './GA4Settings.css';

// SVG Icons
const EyeOpenIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5C7.45455 5 3.57273 7.90909 2 12C3.57273 16.0909 7.45455 19 12 19C16.5455 19 20.4273 16.0909 22 12C20.4273 7.90909 16.5455 5 12 5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const EyeClosedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4L20 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M6.71 6.71C4.19 8.28 2.64 10.47 2 12C3.57 16.09 7.45 19 12 19C13.89 19 15.67 18.5 17.24 17.65" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 5C12.67 5 13.33 5.07 13.97 5.2M19.73 8.27C20.8 9.35 21.58 10.61 22 12C20.43 16.09 16.55 19 12 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9.88 9.88C9.33 10.44 9 11.18 9 12C9 13.66 10.34 15 12 15C12.82 15 13.56 14.67 14.12 14.12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const AlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

// Feature ID for GA4 analytics
const GA4_FEATURE_ID = 'analytics.ga4';

function GA4Settings() {
  const navigate = useNavigate();
  // Feature access check - GA4 is a Pro feature
  const { hasAccess: hasFeatureAccess, loading: featureLoading } = useFeatureAccess(GA4_FEATURE_ID);

  const [config, setConfig] = useState({
    enabled: false,
    measurementId: '',
    apiSecret: '',
    trackEcommerce: true,
    trackPageViews: true,
    trackUserEngagement: true,
    debugMode: false
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [hasExistingSecret, setHasExistingSecret] = useState(false);

  // Fetch config - always try to fetch to show current state
  useEffect(() => {
    fetchConfig();
  }, []);

  // Auto-disable GA4 if user loses feature access (e.g., downgrade)
  useEffect(() => {
    const autoDisableIfNoAccess = async () => {
      // Wait until both feature access and config are loaded
      if (featureLoading || loading) return;

      // If enabled but no longer has access, auto-disable
      if (config.enabled && !hasFeatureAccess) {
        try {
          const response = await fetch('/api/ga4/merchant/config', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ...config, enabled: false })
          });

          if (response.ok) {
            setConfig(prev => ({ ...prev, enabled: false }));
            setSuccessMessage('Google Analytics has been disabled as it is no longer available on your current plan.');
          }
        } catch (err) {
          console.error('Failed to auto-disable GA4:', err);
        }
      }
    };

    autoDisableIfNoAccess();
  }, [featureLoading, loading, hasFeatureAccess, config.enabled]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/ga4/merchant/config', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch GA4 configuration');
      }

      const data = await response.json();
      setConfig(prev => ({
        ...prev,
        enabled: data.enabled || false,
        measurementId: data.measurementId || '',
        trackEcommerce: data.trackEcommerce !== false,
        trackPageViews: data.trackPageViews !== false,
        trackUserEngagement: data.trackUserEngagement !== false,
        debugMode: data.debugMode || false,
        apiSecret: ''
      }));
      setHasExistingSecret(data.hasApiSecret || false);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch GA4 config:', err);
      setError('Failed to load GA4 configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!config.measurementId) {
      setValidationResult({
        isValid: false,
        errors: ['Please enter your Measurement ID']
      });
      return;
    }

    if (!config.apiSecret && !hasExistingSecret) {
      setValidationResult({
        isValid: false,
        errors: ['Please enter your API Secret']
      });
      return;
    }

    setValidating(true);
    setValidationResult(null);

    try {
      const response = await fetch('/api/ga4/merchant/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          measurementId: config.measurementId,
          apiSecret: config.apiSecret || undefined
        })
      });

      const data = await response.json();
      setValidationResult(data);
    } catch (err) {
      setValidationResult({
        isValid: false,
        errors: [err.message || 'Validation failed']
      });
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    if (config.enabled && !config.measurementId) {
      setError('Measurement ID is required when enabling GA4');
      return;
    }

    if (config.enabled && !config.apiSecret && !hasExistingSecret) {
      setError('API Secret is required when enabling GA4');
      return;
    }

    if (config.measurementId && !config.measurementId.startsWith('G-')) {
      setError('Measurement ID must start with "G-" (e.g., G-XXXXXXXXXX)');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch('/api/ga4/merchant/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...config,
          apiSecret: config.apiSecret || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save configuration');
      }

      setSuccessMessage(config.enabled
        ? 'GA4 tracking is now active on your storefront!'
        : 'Settings saved successfully.');

      setValidationResult(null);

      setTimeout(() => {
        fetchConfig();
      }, 1000);
    } catch (err) {
      console.error('Failed to save GA4 config:', err);
      setError(err.message || 'Failed to save configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setValidationResult(null);
    setSuccessMessage(null);
    setError(null);
  };

  // Handle toggle change with feature access check
  const handleToggleChange = (e) => {
    const enabled = e.target.checked;

    // Check feature access when trying to enable
    if (enabled && !hasFeatureAccess) {
      e.target.checked = false;
      navigateToUpgrade(navigate);
      return;
    }

    handleInputChange('enabled', enabled);
  };

  // Show loading while checking feature access
  if (featureLoading || loading) {
    return (
      <div className="ga4-settings ga4-loading">
        <div className="ga4-spinner"></div>
        <p>Loading configuration...</p>
      </div>
    );
  }

  // Google Analytics Icon
  const AnalyticsIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M18 9l-5 5-4-4-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="ga4-settings">
      {/* Alerts */}
      {error && (
        <div className="ga4-alert ga4-alert-error">
          <AlertIcon />
          <span>{error}</span>
          <button className="ga4-alert-close" onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {successMessage && (
        <div className="ga4-alert ga4-alert-success">
          <CheckIcon />
          <span>{successMessage}</span>
          <button className="ga4-alert-close" onClick={() => setSuccessMessage(null)}>&times;</button>
        </div>
      )}

      {/* Main Card - WhatsApp-style integrated header */}
      <div className="ga4-card">
        {/* Card Header with Icon, Title, Toggle */}
        <div className="ga4-card-header">
          <div className="ga4-card-icon">
            <AnalyticsIcon />
          </div>
          <div className="ga4-card-info">
            <h3 className="ga4-card-title">Google Analytics</h3>
            <p className="ga4-card-description">
              Connect Google Analytics 4 to track customer behavior on your store.
            </p>
            <span className={`ga4-status-badge ${config.enabled ? 'ga4-status-enabled' : 'ga4-status-disabled'}`}>
              {config.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <label className="ga4-header-toggle">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={handleToggleChange}
            />
            <span className="ga4-toggle-slider"></span>
          </label>
        </div>

        {/* Configuration fields - only shown when enabled */}
        {config.enabled && (
          <>
            {/* Credentials Section */}
            <div className="ga4-section">
              <h3>GA4 Credentials</h3>

              <div className="ga4-form-group">
                <label>Measurement ID <span className="ga4-required">*</span></label>
                <input
                  type="text"
                  placeholder="G-XXXXXXXXXX"
                  value={config.measurementId}
                  onChange={(e) => handleInputChange('measurementId', e.target.value.trim())}
                  className="ga4-input"
                />
                <span className="ga4-hint">Find this in GA4: Admin &rarr; Data Streams</span>
              </div>

              <div className="ga4-form-group">
                <label>
                  API Secret <span className="ga4-required">*</span>
                  {hasExistingSecret && !config.apiSecret && (
                    <span className="ga4-badge">Saved</span>
                  )}
                </label>
                <div className="ga4-input-wrapper">
                  <input
                    type={showApiSecret ? 'text' : 'password'}
                    placeholder={hasExistingSecret ? 'Enter new secret to update' : 'Enter your API Secret'}
                    value={config.apiSecret}
                    onChange={(e) => handleInputChange('apiSecret', e.target.value.trim())}
                    className="ga4-input"
                  />
                  <button
                    type="button"
                    className="ga4-input-toggle"
                    onClick={() => setShowApiSecret(!showApiSecret)}
                    aria-label={showApiSecret ? 'Hide' : 'Show'}
                  >
                    {showApiSecret ? <EyeOpenIcon /> : <EyeClosedIcon />}
                  </button>
                </div>
                <span className="ga4-hint">Create in GA4: Data Streams &rarr; Measurement Protocol API secrets</span>
              </div>

              {/* Validate Button */}
              <button
                onClick={handleValidate}
                disabled={validating || !config.measurementId || (!config.apiSecret && !hasExistingSecret)}
                className="ga4-btn ga4-btn-secondary"
              >
                {validating ? 'Validating...' : 'Test Connection'}
              </button>

              {validationResult && (
                <div className={`ga4-validation ${validationResult.isValid ? 'ga4-validation-success' : 'ga4-validation-error'}`}>
                  {validationResult.isValid ? (
                    <>
                      <CheckIcon />
                      <span>Connection successful! Your credentials are valid.</span>
                    </>
                  ) : (
                    <>
                      <AlertIcon />
                      <span>{validationResult.errors?.[0] || 'Validation failed'}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Tracking Options */}
            <div className="ga4-section">
              <h3>Tracking Options</h3>

              <div className="ga4-options">
                <label className="ga4-checkbox">
                  <input
                    type="checkbox"
                    checked={config.trackEcommerce}
                    onChange={(e) => handleInputChange('trackEcommerce', e.target.checked)}
                  />
                  <span className="ga4-checkbox-mark"></span>
                  <div className="ga4-checkbox-content">
                    <strong>E-commerce Events</strong>
                    <small>Product views, add to cart, purchases</small>
                  </div>
                </label>

                <label className="ga4-checkbox">
                  <input
                    type="checkbox"
                    checked={config.trackPageViews}
                    onChange={(e) => handleInputChange('trackPageViews', e.target.checked)}
                  />
                  <span className="ga4-checkbox-mark"></span>
                  <div className="ga4-checkbox-content">
                    <strong>Page Views</strong>
                    <small>Track page navigation on your store</small>
                  </div>
                </label>

                <label className="ga4-checkbox">
                  <input
                    type="checkbox"
                    checked={config.trackUserEngagement}
                    onChange={(e) => handleInputChange('trackUserEngagement', e.target.checked)}
                  />
                  <span className="ga4-checkbox-mark"></span>
                  <div className="ga4-checkbox-content">
                    <strong>User Engagement</strong>
                    <small>Scroll depth, time on page</small>
                  </div>
                </label>

                <label className="ga4-checkbox">
                  <input
                    type="checkbox"
                    checked={config.debugMode}
                    onChange={(e) => handleInputChange('debugMode', e.target.checked)}
                  />
                  <span className="ga4-checkbox-mark"></span>
                  <div className="ga4-checkbox-content">
                    <strong>Debug Mode</strong>
                    <small>View events in GA4 DebugView (for testing)</small>
                  </div>
                </label>
              </div>
            </div>

            {/* Save Button */}
            <div className="ga4-actions">
              <button
                onClick={handleSave}
                disabled={saving}
                className="ga4-btn ga4-btn-primary"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>

            {/* Help Links */}
            <div className="ga4-help">
              <span>Need help?</span>
              <a href="https://support.google.com/analytics/answer/9304153" target="_blank" rel="noopener noreferrer">
                GA4 Setup Guide <ExternalLinkIcon />
              </a>
              <a href="https://developers.google.com/analytics/devguides/collection/protocol/ga4" target="_blank" rel="noopener noreferrer">
                API Docs <ExternalLinkIcon />
              </a>
            </div>
          </>
        )}
      </div>

    </div>
  );
}

export default GA4Settings;
